import { makeStyles } from '@mui/styles'
import { IconButton, Tooltip, Typography } from '@mui/material'
import { loadStateFromLocalStorage } from '../redux/utilsLocalStorage'
import InfoIcon from '@mui/icons-material/Info'
import { BlockMath, InlineMath } from 'react-katex'
import 'katex/dist/katex.min.css'

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'baseline',
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  label: {
    fontSize: 14,
    color: theme.palette.primary.main,
    textTransform: 'uppercase',
  },
  value: {
    fontSize: 28,
    fontWeight: '400',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none',
  },
  tooltip: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontSize: theme.typography.fontSize,
  },
  arrow: {
    color: theme.palette.primary.main,
  },
  info: { color: theme.palette.primary.main },
}))

const getNormalizedUnit = unit => {
  const unitMapping = {
    kW: 'kW',
    kWh: 'kW',
    MW: 'MW',
    MWh: 'MW',
  }
  return unitMapping[unit] || 'kW'
}

const METRICS = {
  nmae: {
    unit: '%',
    format: v => v.toFixed(2),
    formula: `
      \\text{NMAE}_{\\%} = \\frac{100}{N \\cdot P_{inst}}
      \\sum_{i=1}^{N} |P_i - F_i|
    `,
    legend: {
      P_i: 'observed power',
      F_i: 'forecasted power',
      'P_{inst}': 'installed capacity',
      N: 'number of time steps',
    },
  },
  nrmse: {
    unit: '%',
    format: v => v.toFixed(2),
    formula: `
      \\text{NRMSE}_{\\%} = \\frac{100}{P_{inst}}
      \\sqrt{\\frac{1}{N} \\sum_{i=1}^{N} (P_i - F_i)^2}
    `,
    legend: {
      P_i: 'observed power',
      F_i: 'forecasted power',
      'P_{inst}': 'installed capacity',
      N: 'number of time steps',
    },
  },
  nbias: {
    unit: '%',
    format: v => v.toFixed(2),
    formula: `
      \\text{NBIAS}_{\\%} = \\frac{100}{N \\cdot P_{inst}}
      \\sum_{i=1}^{N} (F_i - P_i )
    `,
    legend: {
      P_i: 'observed power',
      F_i: 'forecasted power',
      'P_{inst}': 'installed capacity',
      N: 'number of time steps',
      Note: 'A positive value indicates systematic over-forecasting; a negative value indicates under-forecasting',
    },
  },
  mae: {
    unit: selectedUnit => selectedUnit,
    format: v => v.toFixed(2),
    formula: `
      \\text{MAE} = \\frac{1}{N} \\sum_{i=1}^{N} |P_i - F_i|
    `,
    legend: {
      P_i: 'observed (measured) power at time step $i$',
      F_i: 'forecasted power at time step $i$',
      N: 'number of valid time steps included in the evaluation',
    },
  },
  rmse: {
    unit: selectedUnit => selectedUnit,
    format: v => v.toFixed(2),
    formula: `
      \\text{RMSE} = \\sqrt{\\frac{1}{N} \\sum_{i=1}^{N} (P_i - F_i)^2}
    `,
    legend: {
      P_i: 'observed (measured) power at time step $i$',
      F_i: 'forecasted power at time step $i$',
      N: 'number of valid time steps included in the evaluation',
    },
  },
  bias: {
    unit: selectedUnit => selectedUnit,
    format: v => v.toFixed(2),
    formula: `
      \\text{BIAS} = \\frac{1}{N} \\sum_{i=1}^{N} (F_i - P_i)
    `,
    legend: {
      P_i: 'observed (measured) power at time step $i$',
      F_i: 'forecasted power at time step $i$',
      N: 'number of valid time steps included in the evaluation',
    },
  },
  delta: {
    unit: '%',
    format: v => v.toFixed(2),
    formula: `
      \\text{Δ}_{\\%} = \\frac{(S_1 - S_2)}{S_1} \\times 100
    `,
    legend: {
      S_1: 'value from Setup 1',
      S_2: 'value from Setup 2',
      Note: 'Positive values indicate S1 > S2; negative values indicate S1 < S2',
    },
  },
  delta_bias: {
    unit: '%',
    format: v => v.toFixed(2),
    formula: `
      \\text{Δ}_{\\%} = \\frac{|S_1| - |S_2|}{|S_1|} \\times 100
    `,
    legend: {
      S_1: 'value from Setup 1',
      S_2: 'value from Setup 2',
      Note: 'Uses absolute values; positive means |S1| > |S2|',
    },
  },
}

const POWER_METRICS = new Set(['mae', 'rmse', 'bias'])

const renderMathText = text =>
  text
    .split(/(\$.*?\$)/g)
    .map((part, idx) =>
      part.startsWith('$') && part.endsWith('$') ? (
        <InlineMath key={idx} math={part.slice(1, -1)} />
      ) : (
        <span key={idx}>{part}</span>
      ),
    )

const MetricTooltip = ({ metric, classes }) => {
  if (!metric?.formula) return null

  return (
    <Tooltip
      arrow
      placement="top"
      classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
      title={
        <div style={{ textAlign: 'left' }}>
          <BlockMath math={metric.formula} />

          {metric.legend && (
            <div style={{ marginTop: 8, fontSize: '0.85em' }}>
              {Object.entries(metric.legend).map(([symbol, description]) => (
                <div key={symbol}>
                  <InlineMath math={symbol} /> &mdash;{' '}
                  {renderMathText(description)}
                </div>
              ))}
            </div>
          )}
        </div>
      }
    >
      <span>
        <IconButton size="small" disableTouchRipple aria-label="info">
          <InfoIcon fontSize="small" className={classes.info} />
        </IconButton>
      </span>
    </Tooltip>
  )
}

const PerformanceComparisonParameter = ({
  label,
  value,
  compareValue,
  whitespace = false,
}) => {
  const classes = useStyles()

  const storedUnit = loadStateFromLocalStorage('unit') || 'kW'
  const selectedUnit = getNormalizedUnit(storedUnit)

  const formatValue = (metricKey, val) => {
    const m = METRICS[metricKey]
    if (!m || val == null || typeof val !== 'number' || isNaN(val)) return '-'

    const unit = typeof m.unit === 'function' ? m.unit(selectedUnit) : m.unit

    let value = val

    if (selectedUnit === 'MW' && POWER_METRICS.has(metricKey)) {
      value = value / 1000
      value = Number(value.toFixed(2))
    }

    return `${m.format(value)}${unit}`
  }

  const formatLabel = value => {
    if (!value) return value

    if (whitespace) {
      const result = value
        .replace(/([A-Z])/g, ' $1')
        .toLowerCase()
        .trim()

      return result
    }

    return value
  }

  // Calculate percentage difference
  let percentageDiff = null
  let diffColor = '#666'
  let diffSign = ''

  const isValidNumber = val =>
    val != null && typeof val === 'number' && !isNaN(val)

  if (isValidNumber(value) && isValidNumber(compareValue)) {
    // For bias, use absolute values in the calculation
    if (label === 'bias') {
      const absS1 = Math.abs(value)
      const absS2 = Math.abs(compareValue)
      if (absS1 !== 0) {
        percentageDiff = ((absS1 - absS2) / absS1) * 100
      }
    } else if (value !== 0) {
      percentageDiff = ((value - compareValue) / value) * 100
    }
    if (percentageDiff !== null) {
      diffColor = percentageDiff >= 0 ? '#4CAF50' : '#F44336'
      diffSign = percentageDiff >= 0 ? '+' : '-'
    }
  }

  return (
    <div className={classes.container}>
      <div className={classes.labelRow}>
        <Typography className={classes.label}>{formatLabel(label)}</Typography>
        <MetricTooltip metric={METRICS[label]} classes={classes} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography className={classes.value} style={{ fontSize: 22 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#368ED8',
              marginRight: 6,
            }}
          >
            S1
          </span>
          {formatValue(label, value)}
        </Typography>
        <Typography className={classes.value} style={{ fontSize: 22 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#F5A623',
              marginRight: 6,
            }}
          >
            S2
          </span>
          {formatValue(label, compareValue)}
        </Typography>
        {percentageDiff !== null && (
          <Typography
            className={classes.value}
            style={{ fontSize: 18, color: diffColor }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: diffColor,
                marginRight: 6,
              }}
            >
              Δ
            </span>
            {diffSign}
            {Math.abs(percentageDiff).toFixed(2)}%
            <span style={{ position: 'relative', top: -5 }}>
              <MetricTooltip
                metric={label === 'bias' ? METRICS.delta_bias : METRICS.delta}
                classes={classes}
              />
            </span>
          </Typography>
        )}
      </div>
    </div>
  )
}

export default PerformanceComparisonParameter
